import { useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from './AuthContext'
import { isMock } from '../api'
import { EyeIcon } from '../components/icons'
import { providerFrom } from './provider'

/** Through Auth0, the sign-in is Auth0's own page: one button, and the plain words if it did not work. */
function Auth0LoginScreen() {
  const { signIn, signInProblem } = useAuth()
  const [busy, setBusy] = useState(false)
  return (
    <main className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="mark" aria-hidden="true">✓</span>
          <span className="topbar__name">Stello</span>
        </div>
        <div>
          <h1>Sign in</h1>
          <p className="lede">Job Descriptions and Compensation</p>
        </div>
        {signInProblem && <div className="banner banner--bad" role="alert">{signInProblem}</div>}
        <button type="button" className="btn btn--primary" disabled={busy}
          onClick={() => { setBusy(true); void signIn('', '').finally(() => setBusy(false)) }}>
          Sign in
        </button>
      </div>
    </main>
  )
}

export function LoginScreen() {
  if (providerFrom(import.meta.env) === 'auth0') return <Auth0LoginScreen />
  return <PasswordLoginScreen />
}

function PasswordLoginScreen() {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [shown, setShown] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await signIn(email, password)
    } catch (err) {
      // The message never says whether the address exists (FR-A1).
      setError(err instanceof Error ? err.message : 'That email address and password do not match.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="mark" aria-hidden="true">✓</span>
          <span className="topbar__name">Stello</span>
        </div>
        <div>
          <h1>Sign in</h1>
          <p className="lede">Job Descriptions and Compensation</p>
        </div>

        {/* Which backend this build talks to is decided when it is built, and
            is otherwise invisible. Saying so here is the difference between
            "my password is wrong" and "I am looking at a different library". */}
        {isMock && (
          <div className="note" role="status">
            <b>Local demo data</b>
            This build keeps its library in your browser and reaches nothing else. What you
            create here is yours alone and is lost when you clear site data.
          </div>
        )}

        <form onSubmit={submit} noValidate>
          <label className="field">
            <span>Email address</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          {/*
            The other fields wrap their input in the label. This one cannot: a
            button inside a label inherits the label's activation, so a click
            would both toggle and re-focus the field. So the label points at the
            input by id, and the input and the button share a wrapper.
          */}
          <div className="field">
            <label htmlFor="password">
              <span>Password</span>
            </label>
            <div className="field__with-button">
              <input
                id="password"
                type={shown ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="reveal"
                onClick={() => setShown(!shown)}
                // Says what pressing it does, which is what a screen reader
                // user needs, and is read out afresh each time because the
                // label changes with the state.
                aria-label={shown ? 'Hide password' : 'Show password'}
                aria-pressed={shown}
                title={shown ? 'Hide password' : 'Show password'}
              >
                <EyeIcon shown={shown} />
              </button>
            </div>
          </div>

          {error && (
            <div className="banner banner--bad" role="alert">
              <span aria-hidden="true">!</span>
              {error}
            </div>
          )}

          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </main>
  )
}
